<template>
  <div data-qa="game-assets-substep-page">
    <game-assets-upload-files
      v-if="!isReadOnly"
      :allowed-file-names="allowedFileNamesByType"
      :save-endpoint="saveEndpoint"
      @afterSave="refetchData"
      data-qa="game-assets-upload-files"
      :game-campaign-id="gameCampaignId"
    />
    <div data-qa="game-assets-content">
      <div class="search-wrapper" data-qa="game-assets-search-wrapper">
        <el-form-item
          :label="$t('Gamification_Assets_Search')"
          data-qa="game-assets-search-form-item"
        >
          <el-input
            v-model="searchInput"
            class="filter-input"
            :placeholder="$t('Gamification_Assets_Search_Placeholder')"
            prefix-icon="el-icon-search"
            data-qa="game-assets-search-input"
          />
        </el-form-item>
        <div class="buttons-wrapper" data-qa="game-assets-buttons-wrapper">
          <el-button
            type="primary"
            plain
            @click="downloadAllAssets"
            :disabled="!model || !model.data || !model.data.length"
            data-qa="download-all-assets-btn"
          >
            {{ $t('Gamification_Assets_Download_All_Assets') }}
          </el-button>
          <el-button
            type="primary"
            @click="deleteAllAssets"
            :disabled="!model || !model.data || !model.data.length"
            data-qa="delete-all-assets"
          >
            {{ $t('Gamification_Assets_Delete_All_Assets') }}
          </el-button>
        </div>
      </div>
      <rest-page
        ref="stepAssets"
        :fetch-endpoint="fetchEndpoint"
        class="assets-background"
        data-qa="game-assets-rest-page"
      >
        <game-campaign-accordion
          :items="accordionItems"
          :active="activeItems"
          @update="updateActive"
          data-qa="game-assets-accordion"
        >
          <template v-for="item in accordionItems" v-slot:[item.name]>
            <div
              v-if="
                item.name === GameAssetsTemplateEnum.TEAMS &&
                  filteredAssetsTemplates &&
                  filteredAssetsTemplates[GameAssetsTemplateEnum.TEAMS]
              "
              class="card-wrapper"
              data-qa="game-assets-teams-section"
            >
              <game-assets-card
                v-for="asset in filteredAssetsTemplates[
                  GameAssetsTemplateEnum.TEAMS
                ]"
                :key="asset.field"
                :asset="asset"
                :type="GameAssetsTemplateEnum.TEAMS"
                :data-qa="`game-assets-card-team-${asset.label}`"
                :game-campaign-id="gameCampaignId"
                @after-delete="refetchData"
                :is-read-only="isReadOnly"
              />
            </div>
            <div
              v-if="item.name === GameAssetsTemplateEnum.GAME"
              class="card-wrapper"
              data-qa="game-assets-game-section"
            >
              <game-assets-card
                v-for="asset in filteredAssetsTemplates[
                  GameAssetsTemplateEnum.GAME
                ]"
                :key="asset.label"
                :asset="asset"
                :type="GameAssetsTemplateEnum.GAME"
                :data-qa="`game-assets-card-game-${asset.label}`"
                :game-campaign-id="gameCampaignId"
                @after-delete="refetchData"
                :is-read-only="isReadOnly"
              />
            </div>
            <div
              v-if="item.name === GameAssetsTemplateEnum.FONTS"
              class="card-wrapper"
              data-qa="game-assets-fonts-section"
            >
              <game-assets-card
                v-for="asset in filteredAssetsTemplates[
                  GameAssetsTemplateEnum.FONTS
                ]"
                :key="asset.label"
                :asset="asset"
                :type="GameAssetsTemplateEnum.FONTS"
                :data-qa="`game-assets-card-font-${asset.label}`"
                :game-campaign-id="gameCampaignId"
                @after-delete="refetchData"
                :is-read-only="isReadOnly"
              />
            </div>
          </template>
        </game-campaign-accordion>
      </rest-page>
    </div>
    <div v-if="isCreationMode" class="footer" data-qa="game-assets-footer">
      <div class="footer-ct">
        <el-button
          type="primary"
          data-qa="game-assets-next-step-btn"
          @click="goToNextStep"
        >
          {{ $t('Gamification_Assets_Save_Continue') }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </div>
    </div>
    <el-dialog
      :visible="deleteAllLoading"
      center
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <div class="delete-dialog">
        <img src="/img/frameloader.gif" width="48" />
        <div>
          <h2>{{ $t('Gamification_Assets_Delete_Assets') }}</h2>
        </div>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import { debounce } from 'lodash'
import { projectEndpoint, customConfirm, projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'
import GameAssetsUploadFiles from '@/modules/gamification/components/games-campaigns/content-assets/GameAssetsUploadFiles.vue'
import GameAssetsCard from '@/modules/gamification/components/games-campaigns/content-assets/GameAssetsCard.vue'
import GameCampaignAccordion from '@/modules/gamification/components/games-campaigns/GameCampaignAccordion.vue'
import { GameAssetsTemplateEnum } from '@/modules/gamification/enum/GameAssetsTemplateEnum'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'GameStepAssets',
  components: {
    RestPage,
    GameAssetsUploadFiles,
    GameAssetsCard,
    GameCampaignAccordion
  },
  props: {
    isCreationMode: {
      type: Boolean,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    }
  },
  data() {
    return {
      GameAssetsTemplateEnum,
      step: GameCreationSubSteps.ASSETS,
      model: null,
      searchInput: '',
      activeItems: [],
      deleteAllLoading: false
    }
  },
  computed: {
    assetsTemplates() {
      if (!this.model || !this.model.templates) {
        return {}
      }

      const { data, templates } = this.model

      const result = {}

      Object.entries(templates).forEach(([type, items]) => {
        result[type] = items.map(item => {
          const isString = typeof item === 'string'
          const name = isString ? item : item.field
          const label = isString ? item : item.label
          const field = isString ? undefined : item.field

          const match = data.find(
            d => d.name === String(name) && d.type === type
          )

          return match
            ? { label, field, id: match.id, value: match.value }
            : { label, field }
        })
      })

      return result
    },
    filteredAssetsTemplates() {
      const assetsTemplates = this.assetsTemplates
      if (!assetsTemplates) {
        return {}
      }

      const q = (this.searchInput || '').toLowerCase()
      if (!q) {
        return assetsTemplates
      }

      let filtered = {}

      Object.keys(assetsTemplates).forEach(function(type) {
        let items = assetsTemplates[type]

        filtered[type] = items.filter(function(asset) {
          let label = asset.label ? String(asset.label).toLowerCase() : ''
          let field = asset.field ? String(asset.field).toLowerCase() : ''
          return label.includes(q) || field.includes(q)
        })
      })

      return filtered
    },
    allowedFileNamesByType() {
      if (!this.model || !this.model.templates) {
        return {}
      }

      const result = {}

      Object.entries(this.model.templates).forEach(([type, items]) => {
        result[type] = items.map(item => {
          return typeof item === 'string' ? item : String(item.field)
        })
      })

      return result
    },
    accordionItems() {
      if (!this.model || !this.model.templates) {
        return []
      }

      const items = Object.keys(this.model.templates)
      const orderedArray = Object.values(GameAssetsTemplateEnum)

      return items
        .sort((a, b) => orderedArray.indexOf(a) - orderedArray.indexOf(b))
        .map(item => {
          return {
            title: this.$t(`Gamification_Assets_Type_${item}`),
            name: item
          }
        })
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/assets`,
        handler: data => {
          this.model = data
        }
      })
    },
    hasAllAssets() {
      if (!this.model) {
        return false
      }

      const dataNames = this.model.data.map(item => item.name)
      const templates = Object.fromEntries(
        Object.entries(this.model.templates).map(([type, items]) => {
          const mappedItems = items.map(item =>
            typeof item === 'string' ? item : String(item.field)
          )
          return [type, mappedItems]
        })
      )
      const allTemplateValues = Object.values(templates).flat()

      return allTemplateValues.every(name => dataNames.includes(name))
    }
  },
  watch: {
    saveFlag: {
      handler() {
        if (this.isCreationMode) {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
          return
        }
        this.goToNextStep()
      }
    }
  },
  methods: {
    refetchData() {
      this.$refs.stepAssets.reload()
    },
    debouncedRefetch: debounce(function() {
      this.refetchData()
    }, 300),
    downloadAllAssets() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/download-all-assets`,
          responseType: 'blob'
        })
      )
        .then(({ data }) => {
          const blob = new Blob([data], { type: 'application/zip' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'assets.zip'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    deleteAllAssets() {
      if (!this.model.data.length) {
        this.$message({
          message: this.$t('Gamification_Assets_Delete_All_Assets_No_Assets'),
          type: 'error'
        })
        return
      }
      const ids = this.model.data.map(item => item.id)
      this.$confirm(
        this.$t('Gamification_Assets_Delete_Confirmation_Dialog'),
        this.$t('Gamification_Assets_Delete_Confirmation_Dialog_Title'),
        {
          confirmButtonText: this.$t(
            'Gamification_Assets_Delete_Confirmation_Dialog_Confirm'
          ),
          cancelButtonText: this.$t('Campaign_Dialog_Button_Cancel'),
          type: 'warning',
          dangerouslyUseHTMLString: true
        }
      ).then(() => {
        this.deleteAllLoading = true
        RestApi(
          projectEndpoint({
            method: 'delete',
            url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/assets`,
            data: {
              ids: ids
            }
          })
        )
          .then(() => {
            this.debouncedRefetch()
            this.$message({
              message: this.$t('RestList_DeleteSuccessMessage'),
              type: 'success'
            })
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
          .finally(() => {
            this.deleteAllLoading = false
          })
      })
    },
    updateActive(payload) {
      this.activeItems = payload
    },
    goToNextStep() {
      if (this.hasAllAssets) {
        this.$emit('goToNextStep', this.step)
        return
      }

      customConfirm(
        this.$t('Gamification_Assets_Default_Images'),
        this.$t('Gamification_Assets_Default_Images_Description'),
        this.$t('Gamification_Assets_Default_Images_Button_Leave'),
        this.$t('Gamification_Assets_Default_Images_Button_Review_Assets')
      ).then(() => {
        this.$emit('goToNextStep', this.step)
      })
    },
    saveEndpoint(data) {
      let method = 'post'
      if (this.model.data.length) {
        method = 'put'
      }
      return projectEndpoint({
        method: method,
        url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/assets`,
        data: data
      })
    }
  }
}
</script>
<style scoped>
.search-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 32px;
}
.buttons-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}
.card-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
}
.assets-background {
  background: #ffffff;
}
.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
.delete-dialog {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/content-assets/GameStepAssets.vue